import { Employee, EmployeeCluster } from '../types/employee';
import L from 'leaflet';
import { clusterPoints, PixelPoint } from './clustering';

const EARTH_RADIUS_KM = 6371;

export function haversineKm(
  lat1: number, lng1: number,
  lat2: number, lng2: number,
): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export interface TeamDistanceStats {
  avgDistanceKm: number;
  closestOnAverage: Employee;
  mostCentral: Employee;
  mostIsolated: Employee;
  largestCluster: Employee[];
}

const MILES_PER_KM = 0.621371;
// Locales that conventionally use miles (US, UK, Liberia, Myanmar)
const MILE_LOCALES = new Set(['en-US', 'en-GB', 'en-LR', 'my']);

export function localeUsesMiles(): boolean {
  // only consider the preferred locale 
  const locale = navigator.languages?.length ? navigator.languages[0] : navigator.language;
  const base = locale.split('-').slice(0, 2).join('-');
  return MILE_LOCALES.has(base) || MILE_LOCALES.has(locale);
}

export function formatDistance(km: number): string {
  const useMiles = localeUsesMiles();
  const value = useMiles ? km * MILES_PER_KM : km;
  const unit = useMiles ? 'miles' : 'km';
  const threshold = useMiles ? 10000 * MILES_PER_KM : 10000;
  if (value >= threshold) {
    return `${(value / 1000).toFixed(1)}k ${unit}`;
  }
  return `${Math.round(value).toLocaleString()} ${unit}`;
}

// Average flight speed used to convert distance → travel hours for clustering
const KM_PER_HOUR = 900;
const CLUSTER_HOURS = 1;
const CLUSTER_RADIUS_KM = KM_PER_HOUR * CLUSTER_HOURS;

export function computeTeamDistanceStats(employees: Employee[]): TeamDistanceStats | null {
  if (employees.length < 2) return null;

  const coords = employees.map(e => ({
    e,
    lat: e.location.coordinates.latitude,
    lng: e.location.coordinates.longitude,
  }));

  // Pairwise distances — O(n²) but fine for directory-scale teams
  let totalDist = 0;
  let pairs = 0;
  const avgDists: number[] = coords.map(() => 0);

  for (let i = 0; i < coords.length; i++) {
    for (let j = i + 1; j < coords.length; j++) {
      const d = haversineKm(coords[i].lat, coords[i].lng, coords[j].lat, coords[j].lng);
      totalDist += d;
      pairs++;
      avgDists[i] += d;
      avgDists[j] += d;
    }
  }

  const avgDistanceKm = totalDist / pairs;
  const perEmployee = avgDists.map((sum, i) => ({ e: coords[i].e, avg: sum / (coords.length - 1) }));
  const closestOnAverage = perEmployee.reduce((a, b) => (a.avg <= b.avg ? a : b)).e;
  const mostIsolated = perEmployee.reduce((a, b) => (a.avg >= b.avg ? a : b)).e;

  const centerLat = coords.reduce((s, c) => s + c.lat, 0) / coords.length;
  const centerLng = coords.reduce((s, c) => s + c.lng, 0) / coords.length;
  const mostCentral = coords.reduce((best, c) => {
    const d = haversineKm(c.lat, c.lng, centerLat, centerLng);
    const bd = haversineKm(best.lat, best.lng, centerLat, centerLng);
    return d < bd ? c : best;
  }).e;

  // Largest cluster: greedy union-find on pairs within CLUSTER_RADIUS_KM
  const parent = coords.map((_, i) => i);
  function find(x: number): number {
    if (parent[x] !== x) parent[x] = find(parent[x]);
    return parent[x];
  }
  for (let i = 0; i < coords.length; i++) {
    for (let j = i + 1; j < coords.length; j++) {
      if (haversineKm(coords[i].lat, coords[i].lng, coords[j].lat, coords[j].lng) <= CLUSTER_RADIUS_KM) {
        const pi = find(i), pj = find(j);
        if (pi !== pj) parent[pi] = pj;
      }
    }
  }
  const groups = new Map<number, Employee[]>();
  for (let i = 0; i < coords.length; i++) {
    const root = find(i);
    if (!groups.has(root)) groups.set(root, []);
    groups.get(root)!.push(coords[i].e);
  }
  const largestCluster = [...groups.values()].reduce((a, b) => (a.length >= b.length ? a : b));

  return { avgDistanceKm, closestOnAverage, mostCentral, mostIsolated, largestCluster };
}

export interface HQConfig {
  name: string;
  address: string;
  latitude: number;
  longitude: number;
}

export function distanceToHQ(
  employeeLat: number,
  employeeLng: number,
  hq: HQConfig,
): number {
  return haversineKm(employeeLat, employeeLng, hq.latitude, hq.longitude);
}

export function formatDistanceToHQ(km: number, hqName: string): string {
  const useMiles = localeUsesMiles();
  const value = useMiles ? km * MILES_PER_KM : km;
  const unit = useMiles ? 'mi' : 'km';
  let rounded: number;
  if (km > 500) {
    rounded = Math.round(value / 50) * 50;
  } else {
    rounded = Math.round(value / 10) * 10;
  }
  return `~${rounded.toLocaleString()} ${unit} from ${hqName}`;
}

// Common abbreviations for standard (non-DST) UTC offsets
export const TZ_ABBR: Record<number, string> = {
  [-12]: 'UTC-12', [-11]: 'UTC-11', [-10]: 'HST',
  [-9]: 'AKST', [-8]: 'PST', [-7]: 'MST', [-6]: 'CST',
  [-5]: 'EST', [-4]: 'AST', [-3]: 'BRT', [-2]: 'UTC-2',
  [-1]: 'CVT', [0]: 'UTC/GMT', [1]: 'CET', [2]: 'EET',
  [3]: 'MSK', [4]: 'GST', [5]: 'PKT', [5.5]: 'IST',
  [6]: 'BST', [7]: 'ICT', [8]: 'CST/HKT', [9]: 'JST',
  [10]: 'AEST', [11]: 'AEDT', [12]: 'NZST',
};

export function tzLabel(tz: string): string {
  try {
    const ref = new Date('2025-01-15T12:00:00Z');
    const utcStr = ref.toLocaleString('en-US', { timeZone: 'UTC', hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const tzStr = ref.toLocaleString('en-US', { timeZone: tz, hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const parse = (s: string) => {
      const [date, time] = s.split(', ');
      const [m, d, y] = date.split('/');
      return new Date(`${y}-${m}-${d}T${time === '24:00:00' ? '00:00:00' : time}Z`);
    };
    const offsetMs = parse(tzStr).getTime() - parse(utcStr).getTime();
    const offsetH = offsetMs / 3_600_000;
    const sign = offsetH >= 0 ? '+' : '−';
    const abs = Math.abs(offsetH);
    const whole = Math.floor(abs);
    const frac = abs - whole;
    const display = frac ? `${whole}:${String(frac * 60).padStart(2, '0')}` : String(whole);
    const abbr = TZ_ABBR[offsetH];
    return abbr ? `UTC${sign}${display} (${abbr})` : `UTC${sign}${display}`;
  } catch {
    return tz.split('/').pop()!.replace(/_/g, ' ');
  }
}

const AVATAR_SIZE_PX = 32; // 2x radius
const MAX_CLUSTER_ZOOM = 17; // cluster at all zoom levels except the maximum (18)
// Two pins overlap when their centres are closer than one avatar diameter.
const PIN_DIAMETER_PX = AVATAR_SIZE_PX;

export const calculateMapCenter = (employees: Employee[]): [number, number] => {
  if (employees.length === 0) {
    return [0, 0];
  }

  let totalLat = 0;
  let totalLng = 0;

  employees.forEach(employee => {
    totalLat += employee.location.coordinates.latitude;
    totalLng += employee.location.coordinates.longitude;
  });

  const centerLat = totalLat / employees.length;
  const centerLng = totalLng / employees.length;

  return [centerLat, centerLng];
}

// (Over)calculate the size of the avatar in degrees.
function avatarSizeInDegrees(map: L.Map, employee: Employee): number {
  let origin = {lat: employee.location.coordinates.latitude, lng: employee.location.coordinates.longitude}
  let point = map.latLngToContainerPoint(origin)
  let north = map.containerPointToLatLng([point.x, point.y + AVATAR_SIZE_PX]);
  let west = map.containerPointToLatLng([point.x + AVATAR_SIZE_PX, point.y]);

  return Math.max(
    Math.abs(north.lat - origin.lat),
    Math.abs(west.lng - origin.lng),
  );
}

// Build a cluster from a non-empty list of employees, seeding from the first.
function buildCluster(members: Employee[], avatarSize: number): EmployeeCluster {
  const cluster = new EmployeeCluster(members[0], avatarSize);
  for (let i = 1; i < members.length; i++) {
    cluster.addEmployee(members[i]);
  }
  return cluster;
}

export function clusterEmployees(map: L.Map, employees: Employee[], zoom: number): { clusters: EmployeeCluster[]; clusterByEmployeeId: Map<string, EmployeeCluster> } {
  const clusterByEmployeeId = new Map<string, EmployeeCluster>();
  const clusters: EmployeeCluster[] = [];

  if (employees.length === 0 || zoom > MAX_CLUSTER_ZOOM) {
    return { clusters, clusterByEmployeeId };
  }

  // Project every employee to pixel space once (pan-independent layer points).
  // Assumes employee ids are unique (directory invariant); duplicate ids would collapse
  // in byId and mis-count a cluster's members.
  const byId = new Map<string, Employee>();
  const points: PixelPoint[] = employees.map(employee => {
    byId.set(employee.id, employee);
    const pt = map.latLngToLayerPoint([
      employee.location.coordinates.latitude,
      employee.location.coordinates.longitude,
    ]);
    return { id: employee.id, x: pt.x, y: pt.y };
  });

  const groups = clusterPoints(points, PIN_DIAMETER_PX);

  for (const ids of groups) {
    if (ids.length < 2) {
      continue; // singletons render as individual pins
    }
    const members = ids.map(id => byId.get(id)!);
    // One representative avatar size (degrees-per-pixel varies by latitude); members of a
    // cluster are close enough that the first member's size is a fine approximation for the bbox.
    const avatarSize = avatarSizeInDegrees(map, members[0]);
    const cluster = buildCluster(members, avatarSize);
    clusters.push(cluster);
    for (const member of members) {
      clusterByEmployeeId.set(member.id, cluster);
    }
  }

  return { clusters, clusterByEmployeeId };
}
